/**
 * Money.
 *
 * CLAUDE.md Part II §8–9 — money is never a float, every amount carries its
 * currency, and the minor-unit scale comes from the ISO-4217 exponent table.
 * Never assume two decimal places.
 *
 * This module is the ONLY place that formats an amount. A component doing its
 * own `toFixed(2)` is a review-blocking error.
 */

/** An amount is inseparable from its currency. There is no bare-number type here. */
export interface Money {
  /** Integer minor units. Never a float, never a decimal string. */
  readonly amountMinor: bigint
  /** ISO-4217 alphabetic code, uppercase. */
  readonly currency: string
}

/**
 * ISO-4217 exponents that are NOT 2. Anything absent defaults to 2.
 *
 * Keeping the exceptions rather than the full table is deliberate: the list of
 * non-2 currencies is short and stable, and a missing entry degrades to the
 * common case instead of throwing on an unknown code.
 */
const EXPONENT_EXCEPTIONS: Readonly<Record<string, number>> = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
  PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  CLF: 4,
}

export function exponentFor(currency: string): number {
  return EXPONENT_EXCEPTIONS[currency.toUpperCase()] ?? 2
}

/**
 * Whether a string can be used as a currency at all.
 *
 * Exported because `money()` THROWS on a bad code, and a throw is right there —
 * an amount whose currency we cannot name is not a value this system may carry
 * (invariant 4), and returning a default would be inventing one. But a throw
 * inside a React render takes the whole screen to the error boundary, so an
 * adapter reading a currency off the wire has to ask BEFORE it builds a block
 * rather than discover it while drawing.
 *
 * Same predicate `money()` applies, in one place, so the two cannot drift.
 */
export function isCurrencyCode(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Z]{3}$/.test(value.trim().toUpperCase())
}

/**
 * The wire form of `amount_minor`: a JSON number, or a decimal string.
 *
 * Both, because the backend is moving between them (go-wallet#377) and the
 * console has to read either one during and after that move. A reader that
 * accepted only the shape being replaced would turn a backend deploy into an
 * operator-facing outage, and go-wallet's staging deploys on merge.
 */
export type MinorWire = number | string

/**
 * Read `amount_minor` off the wire, or refuse it.
 *
 * # Why a string is the destination shape
 *
 * A JSON number is an IEEE-754 double in every JavaScript runtime, so
 * `JSON.parse` rounds a Go `int64` past 2^53-1 **before any code here runs**.
 * The digits are gone by the time the value exists and no check can recover
 * them — which is why the backend is moving to a string, and why this must be
 * able to read one.
 *
 * # Why BigInt, and not Number()
 *
 * This is the part that looks like a detail and is the whole point.
 * `Number("9007199254740993")` is `9007199254740992` — the input rounds during
 * the conversion, so `Number.isSafeInteger(Number(s))` answers `true` for the
 * exact value that must be refused. Validating the parsed double would accept
 * precisely the input this function exists to catch. So the bound is checked in
 * BigInt, where nothing rounds, and only then narrowed.
 *
 * Refusing is safe here and rendering is not: a figure this returns `null` for
 * routes to "cannot be stated", while a number wrong in its last places looks
 * entirely plausible on a reconciliation screen.
 */
export function exactMinor(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? value : null
  }
  if (typeof value !== 'string') return null

  // Decimal integer only. No exponent, no decimal point, no whitespace, no
  // `0x`: `BigInt` accepts several of those and each one would let a shape the
  // backend never emits through a guard whose job is to be narrow.
  if (!/^-?\d+$/.test(value)) return null

  const minor = BigInt(value)
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < BigInt(Number.MIN_SAFE_INTEGER)) {
    return null
  }
  return Number(minor)
}

/**
 * Read `amount_minor` off the wire as a bigint, or refuse it.
 *
 * The sibling of `exactMinor`, for callers that hold the value as a `bigint`
 * rather than a `number` — which is most of the ledger screens, because
 * `money()` and `format()` are bigint the whole way down.
 *
 * The difference is the 2^53 bound, and it is deliberate rather than an
 * oversight: a `number` consumer must refuse anything past `MAX_SAFE_INTEGER`
 * because it cannot hold it, and a `bigint` consumer can hold it exactly, so
 * refusing would discard a figure it is perfectly able to state. What neither
 * may do is accept a JSON *number* past the bound — by then `JSON.parse` has
 * already rounded it and the digits are gone, so `BigInt(value)` would faithfully
 * preserve the wrong integer. Hence the string branch is unbounded and the
 * number branch is not.
 *
 * Same strict decimal pattern as `exactMinor`, and the same reason: `BigInt()`
 * alone accepts `"0x10"`, `"1e3"` and — worst — `""` as zero, which would render
 * a blank field as a balance.
 */
export function exactMinorBig(value: unknown): bigint | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? BigInt(value) : null
  }
  if (typeof value !== 'string') return null
  if (!/^-?\d+$/.test(value)) return null
  return BigInt(value)
}

export function money(amountMinor: bigint | number | string, currency: string): Money {
  const code = currency.trim().toUpperCase()
  if (!isCurrencyCode(code)) {
    throw new Error(`invalid ISO-4217 currency code: ${JSON.stringify(currency)}`)
  }
  const minor = typeof amountMinor === 'bigint' ? amountMinor : BigInt(amountMinor)
  return { amountMinor: minor, currency: code }
}

export function isNegative(value: Money): boolean {
  return value.amountMinor < 0n
}

export function isZero(value: Money): boolean {
  return value.amountMinor === 0n
}

/** Refuses to operate across currencies. Invariant 4: no currency-less comparison. */
function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(`refusing to combine ${a.currency} with ${b.currency}`)
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return { amountMinor: a.amountMinor + b.amountMinor, currency: a.currency }
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return { amountMinor: a.amountMinor - b.amountMinor, currency: a.currency }
}

/** Returns whichever is smaller. This is the FR-5.4 `min()` used for available balance. */
export function min(a: Money, b: Money): Money {
  assertSameCurrency(a, b)
  return a.amountMinor <= b.amountMinor ? a : b
}

export function compare(a: Money, b: Money): number {
  assertSameCurrency(a, b)
  if (a.amountMinor < b.amountMinor) return -1
  if (a.amountMinor > b.amountMinor) return 1
  return 0
}

/** Splits into the sign and the unsigned digit string, scaled by the exponent. */
function decompose(value: Money): { negative: boolean; digits: string } {
  const exponent = exponentFor(value.currency)
  const negative = value.amountMinor < 0n
  const absolute = (negative ? -value.amountMinor : value.amountMinor).toString()

  if (exponent === 0) return { negative, digits: absolute }

  const padded = absolute.padStart(exponent + 1, '0')
  const whole = padded.slice(0, padded.length - exponent)
  const fraction = padded.slice(padded.length - exponent)
  return { negative, digits: `${whole}.${fraction}` }
}

export interface FormatOptions {
  /** BCP-47 locale for grouping and decimal separators. Defaults to en-GB. */
  readonly locale?: string
  /** Render the ISO code alongside the number. Defaults to true. */
  readonly withCurrency?: boolean
}

/**
 * Formats for display. The currency code is rendered ADJACENT to the number
 * rather than merged into it, so the pairing stays visible at a glance and a
 * copied value keeps its currency.
 */
export function format(value: Money, options: FormatOptions = {}): string {
  const { locale = 'en-GB', withCurrency = true } = options
  const exponent = exponentFor(value.currency)
  const { negative, digits } = decompose(value)

  const [wholePart = '0', fractionPart] = digits.split('.')
  const grouped = new Intl.NumberFormat(locale, { useGrouping: true }).format(BigInt(wholePart))
  const separator = new Intl.NumberFormat(locale).format(1.1).charAt(1)

  const magnitude = exponent === 0 ? grouped : `${grouped}${separator}${fractionPart ?? ''}`
  const signed = negative ? `−${magnitude}` : magnitude

  return withCurrency ? `${signed} ${value.currency}` : signed
}

/**
 * Parses operator input in major units into minor units, exactly.
 *
 * String arithmetic throughout — routing user input through a JS number would
 * reintroduce the float this whole module exists to avoid.
 */
export function parseMajor(input: string, currency: string): Money {
  const code = currency.trim().toUpperCase()
  const exponent = exponentFor(code)
  // Strip grouping noise an operator may paste: ordinary spaces, non-breaking
  // and narrow non-breaking spaces (fr-FR / de-CH grouping), and apostrophes.
  const trimmed = input.trim().replace(/[\s\u00A0\u202F']/g, '')

  const match = /^(-|−)?(\d*)(?:[.,](\d*))?$/.exec(trimmed)
  if (match === null || (match[2] === '' && (match[3] ?? '') === '')) {
    throw new Error(`not a valid amount: ${JSON.stringify(input)}`)
  }

  const negative = match[1] !== undefined
  const whole = match[2] === '' ? '0' : (match[2] ?? '0')
  const fraction = match[3] ?? ''

  if (fraction.length > exponent) {
    throw new Error(`${code} allows at most ${exponent} decimal place(s); got "${input}"`)
  }

  const minor = BigInt(whole + fraction.padEnd(exponent, '0'))
  return { amountMinor: negative ? -minor : minor, currency: code }
}
